import { useState, useEffect } from 'react';
import { api } from '../lib/api';
import Button from './ui/Button';

/**
 * FeedbackThread — comments + collaborators for a single feedback item.
 * Admin-only collaborator management; all authenticated users can comment.
 */
export default function FeedbackThread({ feedbackId, isAdmin = false }) {
  const [comments, setComments] = useState([]);
  const [collaborators, setCollaborators] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [newCollaborator, setNewCollaborator] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  function loadCollaborators() {
    return api.get(`/feedback/${feedbackId}/collaborators`).then((d) => setCollaborators(d.collaborators || [])).catch(() => {});
  }

  useEffect(() => {
    if (!feedbackId) return;
    setLoading(true);
    Promise.all([
      api.get(`/feedback/${feedbackId}/comments`).then((d) => setComments(d.comments || [])).catch(() => {}),
      loadCollaborators(),
    ])
      .finally(() => setLoading(false));
  }, [feedbackId]);

  async function postComment() {
    if (!newComment.trim()) return;
    setSubmitting(true);
    try {
      const res = await api.post(`/feedback/${feedbackId}/comments`, { body: newComment.trim() });
      if (res && res.id) setComments((prev) => [...prev, res]);
      setNewComment('');
    } catch {} finally { setSubmitting(false); }
  }

  async function addCollaborator() {
    if (!newCollaborator.trim()) return;
    setSubmitting(true);
    try {
      await api.post(`/feedback/${feedbackId}/collaborators`, { email: newCollaborator.trim() });
      await loadCollaborators();
      setNewCollaborator('');
    } catch {} finally { setSubmitting(false); }
  }

  async function removeCollaborator(collab) {
    try {
      await api.delete(`/feedback/${feedbackId}/collaborators/${encodeURIComponent(collab.id)}`);
      setCollaborators((prev) => prev.filter((c) => c.id !== collab.id));
    } catch {}
  }

  if (loading) return <div className="text-sm text-gray-400 py-2">Loading thread…</div>;

  return (
    <div className="space-y-4">
      {isAdmin && (
        <div>
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Collaborators</p>
          <div className="flex flex-wrap gap-2 mb-2">
            {collaborators.map((c) => (
              <span key={c.user_email} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 text-xs">
                {c.user_email}
                <button onClick={() => removeCollaborator(c)} className="ml-1 text-blue-400 hover:text-red-500">&times;</button>
              </span>
            ))}
          </div>
          <div className="flex gap-2">
            <input
              value={newCollaborator}
              onChange={(e) => setNewCollaborator(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && addCollaborator()}
              placeholder="Add collaborator email…"
              className="flex-1 rounded-md border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
            />
            <Button size="sm" onClick={addCollaborator} disabled={submitting || !newCollaborator.trim()}>Add</Button>
          </div>
        </div>
      )}

      <div>
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Comments</p>
        <div className="space-y-3 mb-3 max-h-48 overflow-y-auto">
          {comments.length === 0 && <p className="text-sm text-gray-400">No comments yet.</p>}
          {comments.map((c) => (
            <div key={c.id} className="text-sm">
              <div className="flex items-center gap-2 mb-0.5">
                <span className="font-medium text-gray-700 dark:text-gray-300">{c.user_email || 'User'}</span>
                <span className="text-xs text-gray-400">{new Date(c.created_at).toLocaleDateString()}</span>
              </div>
              <p className="text-gray-600 dark:text-gray-400 whitespace-pre-wrap">{c.body}</p>
            </div>
          ))}
        </div>
        <div className="flex gap-2 items-end">
          <textarea
            rows={2}
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); postComment(); } }}
            placeholder="Add a comment…"
            className="flex-1 rounded-md border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary/50"
          />
          <Button size="sm" onClick={postComment} disabled={submitting || !newComment.trim()}>Post</Button>
        </div>
      </div>
    </div>
  );
}
